import React, { useState } from 'react';
import { X, Building2, Mail, Phone, MapPin, FileText, Lock, Users, Shield, CreditCard } from 'lucide-react';

const CreateOrganizationModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    address: '',
    gst_no: '',
    video_sessions_enabled: true,
    theraptrack_controlled: false,
    number_of_therapists: '',
    password: '',
    referral_code: '',
    referral_code_discount: '',
    referral_code_discount_type: 'percentage',
    hide_therapists_tab: false,
    hide_questionnaires_tab: false,
    disable_therapist_plan_change: false,
    show_therapist_client_details: true,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };


  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.contact.trim()) {
      newErrors.contact = 'Contact number is required';
    } else if (!/^\d{10}$/.test(formData.contact.replace(/\D/g, ''))) {
      newErrors.contact = 'Contact number must be 10 digits';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      // Convert empty strings to null for optional numeric fields
      const submitData = {
        ...formData,
        number_of_therapists: formData.number_of_therapists === '' ? null : (formData.number_of_therapists ? parseInt(formData.number_of_therapists, 10) : null),
        gst_no: formData.gst_no === '' ? null : formData.gst_no,
        referral_code: formData.referral_code === '' ? null : formData.referral_code,
        referral_code_discount: formData.referral_code_discount === '' ? null : parseFloat(formData.referral_code_discount),
        referral_code_discount_type: formData.referral_code_discount === '' ? null : formData.referral_code_discount_type,
      };
      onSubmit(submitData);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      contact: '',
      address: '',
      gst_no: '',
      video_sessions_enabled: true,
      theraptrack_controlled: false,
      number_of_therapists: '',
      password: '',
      referral_code: '',
      referral_code_discount: '',
      referral_code_discount_type: 'percentage',
      hide_therapists_tab: false,
      hide_questionnaires_tab: false,
      disable_therapist_plan_change: false,
      show_therapist_client_details: true,
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-xl w-full max-w-full sm:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-dark-bg-tertiary border-b border-gray-200 dark:border-dark-border px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-dark-text-primary flex items-center">
            <Building2 className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-primary-700" />
            <span className="hidden sm:inline">Create New Organization</span>
            <span className="sm:hidden">New Organization</span>
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 dark:text-dark-text-tertiary hover:text-gray-600 dark:hover:text-dark-text-secondary transition-colors"
            disabled={isLoading}
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
          {/* Organization Name */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Organization Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter organization name"
                disabled={isLoading}
              />
            </div>
            {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="organization@example.com"
                disabled={isLoading}
              />
            </div>
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          </div>

          {/* Contact Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.contact ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="1234567890"
                disabled={isLoading}
              />
            </div>
            {errors.contact && <p className="mt-1 text-sm text-red-500">{errors.contact}</p>}
            <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-tertiary">
              Use your WhatsApp number so you can receive client bookings, appointments, updates and reminders.
            </p>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows="3"
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.address ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter full address"
                disabled={isLoading}
              />
            </div>
            {errors.address && <p className="mt-1 text-sm text-red-500">{errors.address}</p>}
          </div>

          {/* GST Number (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              GST Number (Optional)
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="gst_no"
                value={formData.gst_no}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="GST registration number"
                disabled={isLoading}
              />
            </div>
          </div>


          {/* Video Sessions Toggle */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  name="video_sessions_enabled"
                  checked={formData.video_sessions_enabled}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-700 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">
                  Enable Video Sessions
                </span>
                <p className="text-xs text-gray-600 mt-1">
                  Allow partners in this organization to create and manage video sessions with their clients.
                  When disabled, existing sessions remain accessible via direct link but cannot be managed.
                </p>
              </div>
            </label>
          </div>

          {/* TheraPTrack Controlled */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  name="theraptrack_controlled"
                  checked={formData.theraptrack_controlled}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-700 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center">
                  <Shield className="h-4 w-4 text-primary-700 mr-2" />
                  <span className="text-sm font-medium text-gray-900">
                    TheraPTrack Controlled
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  If enabled, therapists in this organization can view subscription details in their settings.
                  If disabled, only organization users can see subscription information.
                </p>
              </div>
            </label>
          </div>

          {/* Tab Visibility Settings - Only shown if TheraPTrack Controlled is enabled */}
          {formData.theraptrack_controlled && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center mb-2">
                <Users className="h-4 w-4 text-purple-700 mr-2" />
                <span className="text-sm font-medium text-gray-900">
                  Tab Visibility Settings
                </span>
              </div>
              <p className="text-xs text-gray-600 mb-3">
                Control which tabs are visible in the organization dashboard. When checked, the selected tabs will be hidden from navigation.
              </p>
              
              <div className="space-y-2">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      name="hide_therapists_tab"
                      checked={formData.hide_therapists_tab}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-700 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-medium text-gray-900">
                      Hide Therapists Management Tab
                    </span>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Hide the Therapists Management tab from the organization dashboard navigation.
                    </p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      name="hide_questionnaires_tab"
                      checked={formData.hide_questionnaires_tab}
                      onChange={handleChange}
                      className="w-4 h-4 text-primary-700 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-medium text-gray-900">
                      Hide Questionnaires Tab
                    </span>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Hide the Questionnaires tab from the organization dashboard navigation.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Disable Therapist Plan Change - Only shown if TheraPTrack Controlled is enabled */}
          {formData.theraptrack_controlled && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    name="disable_therapist_plan_change"
                    checked={formData.disable_therapist_plan_change}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-700 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                    disabled={isLoading}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center">
                    <CreditCard className="h-4 w-4 text-blue-700 mr-2" />
                    <span className="text-xs font-medium text-gray-900">
                      Disable Therapist Plan Change
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    When checked, therapists in this organization will not be able to change their subscription plans from the Subscription Management tab.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Show Therapist Client Details - Only shown if TheraPTrack Controlled is enabled */}
          {formData.theraptrack_controlled && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <label className="flex items-start space-x-3 cursor-pointer">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    name="show_therapist_client_details"
                    checked={formData.show_therapist_client_details}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-700 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                    disabled={isLoading}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center">
                    <Users className="h-4 w-4 text-indigo-700 mr-2" />
                    <span className="text-xs font-medium text-gray-900">
                      Show Therapist Client Details
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">
                    When checked, organization admins can see client names and details when a therapist is selected in the Therapists Management tab. When unchecked, client information will be hidden.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Referral Code (Only for TheraPTrack Controlled) */}
          {formData.theraptrack_controlled && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referral Code (Optional)
                </label>
                <input
                  type="text"
                  name="referral_code"
                  value={formData.referral_code}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg uppercase focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., WELCOME2024"
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-600 mt-1">
                  Enter a referral code for therapists to use when joining your organization during signup.
                </p>
              </div>

              {/* Discount Fields (Only show if referral code is entered) */}
              {formData.referral_code && (
                <div className="space-y-3 pl-4 border-l-2 border-blue-300">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount Amount
                      </label>
                      <input
                        type="number"
                        name="referral_code_discount"
                        value={formData.referral_code_discount}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="0"
                        disabled={isLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Discount Type
                      </label>
                      <select
                        name="referral_code_discount_type"
                        value={formData.referral_code_discount_type}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        disabled={isLoading}
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount (₹)</option>
                      </select>
                    </div>
                  </div>
                  {formData.referral_code_discount && (
                    <div className="bg-white rounded p-2 text-sm">
                      <span className="font-medium text-green-700">
                        Discount Preview: {' '}
                        {formData.referral_code_discount_type === 'percentage'
                          ? `${formData.referral_code_discount}% off`
                          : `₹${formData.referral_code_discount} off`}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Number of Therapists */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Therapists (Optional)
            </label>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="number"
                name="number_of_therapists"
                value={formData.number_of_therapists}
                onChange={handleChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter number of therapists"
                min="1"
                disabled={isLoading}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              This will be used to calculate subscription pricing
            </p>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Initial Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter initial password"
                disabled={isLoading}
              />
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
            <p className="mt-1 text-xs text-gray-500">
              The organization will use this password for initial login
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrganizationModal;

